import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  Stack,
  Divider,
  useTheme,
} from '@mui/material';

const Footer = () => {
  const theme = useTheme();
  const [backendVersion, setBackendVersion] = useState('loading...');

  const currentYear = new Date().getFullYear();
  
  // Get build version from environment or use a default
  const buildVersion = process.env.REACT_APP_BUILD_VERSION || 'dev';
  const buildDate = process.env.REACT_APP_BUILD_DATE || new Date().toISOString().split('T')[0];

  // Fetch backend version
  useEffect(() => {
    const fetchBackendVersion = async () => {
      try {
        const response = await fetch('https://api.textsimpler.com/api/version');
        const data = await response.json();
        setBackendVersion(data.buildVersion || 'unknown');
      } catch (error) {
        console.error('Failed to fetch backend version:', error);
        setBackendVersion('error');
      }
    };

    fetchBackendVersion();
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'background.paper',
        borderTop: `1px solid ${theme.palette.divider}`,
        py: 4,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          {/* Main Footer Content */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 3,
            }}
          >
            {/* Brand Section */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                TextSimpler
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                Transform your text with AI-powered tools. Professional, fast, and secure text analysis for everyone.
              </Typography>
            </Box>

            {/* Quick Links */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              sx={{ textAlign: { xs: 'center', md: 'left' } }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Tools
                </Typography>
                <Stack spacing={0.5}>
                  <Link
                    href="/SentimentAnalyzer"
                    color="text.secondary"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    Sentiment Analyzer
                  </Link>
                  <Link
                    href="/HashtagsAnalyzer"
                    color="text.secondary"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    Hashtag Generator
                  </Link>
                  <Link
                    href="/EmojisAnalyzer"
                    color="text.secondary"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    Emoji Suggester
                  </Link>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Support
                </Typography>
                <Stack spacing={0.5}>
                  <Link
                    href="/provide-feedback"
                    color="text.secondary"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    Feedback
                  </Link>
                  <Link
                    href="/privacy-policy"
                    color="text.secondary"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    href="/terms-and-conditions"
                    color="text.secondary"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    Terms & Conditions
                  </Link>
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Bottom Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {currentYear} TextSimpler. All rights reserved.
            </Typography>
            
            <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
              <Typography variant="body2" color="text.secondary">
                Built with ❤️ using React & Material-UI
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Frontend v{buildVersion} • Backend v{backendVersion} • {buildDate}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
